import Starling from '@ies/starling_client';
import { i18n } from './i18n';
import { starlingLog } from './log';
import { Resources, Config } from './typings';

type StartlingConfig = Pick<
  Config,
  | 'zoneHost'
  | 'TEAChannelDomain'
  | 'test'
  | 'starlingProjects'
  | 'defaultLocale'
  | 'lazyUpdate'
  | 'mode'
  | 'format'
  | 'timeout'
  | 'store'
> & {
  locale: string;
  debug?: boolean;
};

// EN => en, eN => en, EN_us => en_US`
export function formatLng<T>(lng: T): T | string {
  if (typeof lng !== 'string') {
    return lng;
  }

  return lng.replace(/([a-zA-Z]+)(_[a-zA_Z]+)?/, function(_$0, $1, $2) {
    return $1.toLowerCase() + ($2 ? $2.toUpperCase() : '');
  });
}

async function StarlingClient({
  starlingProjects = [],
  locale,
  test,
  zoneHost,
  store,
  TEAChannelDomain,
  lazyUpdate,
  mode,
  format,
  timeout,
  debug,
}: StartlingConfig) {
  const promiseList: Array<Promise<any>> = [];

  const nsShouldAddPrefix = starlingProjects.length > 1;

  starlingProjects.forEach(({ namespace, api_key, projectName, version }) => {
    promiseList.push(
      ...((namespace || []) as string[]).map(async (n: string) => {
        const starlingParams = {
          api_key,
          locale,
          namespace: n,
          test,
          zoneHost: zoneHost!,
          TEAChannelDomain,
          store,
          // 如果没有改语言的翻译文案，则请求降级语言的翻译文案
          fallbackLang: [],
          lazyUpdate,
          mode,
          format,
          timeout,
          version,
        };
        if (debug) {
          starlingLog('Starling Client Params:', starlingParams);
        }
        const starling = new Starling(starlingParams);

        try {
          const resource = await new Promise((resolve, reject) =>
            starling.load(resolve, reject),
          );

          if (debug) {
            starlingLog('Starling Client Result:', resource);
          }

          if (!resource || Object.keys(resource as any).length === 0) {
            return null;
          }

          return {
            [nsShouldAddPrefix ? `${projectName}_${n}` : n]: resource,
          };
        } catch (e) {
          return null;
        }
      }),
    );
  });

  try {
    const res = await Promise.all(promiseList);

    if (
      res.every(item => {
        return !item || Object.keys(item).length === 0;
      })
    ) {
      return null;
    }

    return res.reduce((resources: any, item) => {
      resources = {
        ...resources,
        ...item,
      };
      return resources;
    }, {});
  } catch (e) {
    return null;
  }
}

// browser, 从 starling 平台拉取语言，并且更新 i18n
export async function StarlingLoad({
  defaultLocale,
  locale,
  test,
  zoneHost,
  TEAChannelDomain,
  starlingProjects = [],
  lazyUpdate = false,
  mode = 'normal',
  format = 'merged',
  timeout = 5000,
  store,
  debug = false,
}: StartlingConfig): Promise<{ resources: Resources; lng: string }> {
  let lng = formatLng(locale);
  let resources: any = await StarlingClient({
    locale,
    test,
    zoneHost,
    TEAChannelDomain,
    starlingProjects,
    lazyUpdate,
    mode,
    format,
    timeout,
    store,
    debug,
  });

  if (
    !resources &&
    defaultLocale &&
    defaultLocale !== lng &&
    !i18n.getDataByLanguage(locale)
  ) {
    // eslint-disable-next-line require-atomic-updates
    resources = await StarlingClient({
      locale: defaultLocale,
      test,
      zoneHost,
      TEAChannelDomain,
      starlingProjects,
      lazyUpdate,
      mode,
      format,
      timeout,
      store,
      debug,
    });
    lng = defaultLocale;
  }

  Object.keys(resources || {}).forEach(ns => {
    i18n.addResourceBundle(lng, ns, resources[ns]);
  });

  i18n.changeLanguage(lng);
  return {
    lng,
    resources: {
      [lng]: resources,
    },
  };
}
